import {
  isAllergyStatusIcd10Code,
  isFamilyHistoryIcd10Code,
  isMalignantNeoplasmIcd10Code,
  isOtherHistoryAndStatusIcd10Code,
  isPersonalHistoryIcd10Code,
  isSecondaryMalignantNeoplasmIcd10Code,
  isSymptomIcd10Code,
  isUnspecifiedIcd10Code,
} from "./icd10";
import { canonicalClinicalCode, clinicalCodeCategory, isCategoryInRange } from "./normalise";

export type JudgeGroup = { label: string; codes: string[] };

export type DescriptionLookup = (code: string) => string | undefined;

type CodeRule = (code: string) => boolean;

const inRange =
  (chapter: string, min: number, max: number): CodeRule =>
  (code) =>
    isCategoryInRange(code, chapter, min, max);

const inCategories =
  (...categories: string[]): CodeRule =>
  (code) =>
    categories.includes(clinicalCodeCategory(code));

const isExactCode =
  (exact: string): CodeRule =>
  (code) =>
    canonicalClinicalCode(code) === exact;

const anyOf =
  (...rules: CodeRule[]): CodeRule =>
  (code) =>
    rules.some((rule) => rule(code));

const unique = <T>(items: readonly T[]): T[] => [...new Set(items)];

const pairsOf = <T>(items: readonly T[]): [T, T][] =>
  items.flatMap((first, index) => items.slice(index + 1).map((second): [T, T] => [first, second]));

/** Merges groups that share a code, so a code is in one group; codes and groups keep the order of `order`. */
const mergeLinked = (groups: readonly JudgeGroup[], order: readonly string[]): JudgeGroup[] => {
  const merged = groups.reduce<JudgeGroup[]>((acc, group) => {
    const overlapping = acc.filter((existing) => existing.codes.some((code) => group.codes.includes(code)));
    const separate = acc.filter((existing) => !overlapping.includes(existing));
    const labels = unique([...overlapping.map((existing) => existing.label), group.label]);
    const codes = unique([...overlapping.flatMap((existing) => existing.codes), ...group.codes]);

    return [...separate, { label: labels.join(" / "), codes }];
  }, []);

  const position = (code: string) => order.indexOf(code);
  return merged
    .map(({ label, codes }) => ({ label, codes: [...codes].sort((a, b) => position(a) - position(b)) }))
    .sort((a, b) => position(a.codes[0] ?? "") - position(b.codes[0] ?? ""));
};

/** `J30`, `J43`, `J44`, `J45` — the asthma/COPD rules' categories, which include vasomotor and allergic rhinitis (`J30`) alongside emphysema, other COPD and asthma. */
export const getAsthmaCopdCodeCategories = (): readonly string[] => ["J30", "J43", "J44", "J45"];

/** `K20`, `K21` — oesophagitis and gastro-oesophageal reflux disease. */
export const getRefluxOesophagitisCodeCategories = (): readonly string[] => ["K20", "K21"];

export const isAsthmaCopdIcd10Code: CodeRule = (code) => inCategories(...getAsthmaCopdCodeCategories())(code);

export const isRefluxOesophagitisIcd10Code: CodeRule = (code) =>
  inCategories(...getRefluxOesophagitisCodeCategories())(code);

/** Codes a rule owns, or coders keep as written, so the judge never sees them. */
export const isNeverSentToJudge: CodeRule = anyOf(
  isSymptomIcd10Code,
  isAsthmaCopdIcd10Code,
  isRefluxOesophagitisIcd10Code,
  isFamilyHistoryIcd10Code,
  isPersonalHistoryIcd10Code,
  isAllergyStatusIcd10Code,
  isOtherHistoryAndStatusIcd10Code,
);

const CODERS_KEEP_BOTH: readonly { unspecified: string; specifics: readonly string[] }[] = [
  { unspecified: "I25.9", specifics: ["I25.1", "I25.2"] },
  { unspecified: "K64.9", specifics: ["K64.4"] },
  { unspecified: "K59.9", specifics: ["K59.0"] },
];

const codersKeepBoth = (first: string, second: string): boolean => {
  const [a, b] = [canonicalClinicalCode(first), canonicalClinicalCode(second)];

  return CODERS_KEEP_BOTH.some(
    ({ unspecified, specifics }) =>
      (a === unspecified && specifics.includes(b)) || (b === unspecified && specifics.includes(a)),
  );
};

const membersByCategory = (codes: readonly string[]) =>
  codes.reduce((byCategory, code) => {
    const category = clinicalCodeCategory(code);
    return byCategory.set(category, [...(byCategory.get(category) ?? []), code]);
  }, new Map<string, string[]>());

export const groupCodesByCategory = (codes: readonly string[], descriptionOf: DescriptionLookup): JudgeGroup[] => {
  const isUnspecified = (code: string) => isUnspecifiedIcd10Code(code, descriptionOf(code));
  const pairs = [...membersByCategory(unique(codes))].flatMap(([category, members]) =>
    pairsOf(members)
      .filter(([first, second]) => isUnspecified(first) || isUnspecified(second))
      .filter(([first, second]) => !codersKeepBoth(first, second))
      .map((pair): JudgeGroup => ({ label: category, codes: pair })),
  );

  return mergeLinked(pairs, codes);
};

const FAMILIES: readonly { label: string; matches: CodeRule }[] = [
  { label: "Diabetes", matches: inRange("E", 10, 14) },
  { label: "Anaemia", matches: inRange("D", 50, 64) },
  { label: "Depression", matches: anyOf(inCategories("F32", "F33"), isExactCode("F41.2")) },
  { label: "Liver", matches: inRange("K", 70, 77) },
  { label: "Alcohol", matches: inCategories("F10") },
  { label: "Colitis", matches: anyOf(inCategories("K50", "K51", "K52"), isExactCode("K63.3")) },
  { label: "Peptic ulcer", matches: inRange("K", 25, 28) },
  { label: "Implants and devices", matches: inRange("Z", 95, 97) },
  { label: "Arrhythmia", matches: inRange("I", 47, 49) },
  { label: "Kidney", matches: inCategories("N18", "N19") },
  { label: "Dermatitis", matches: inRange("L", 20, 30) },
  { label: "Hearing loss", matches: inCategories("H90", "H91") },
  { label: "Lymphoma", matches: inRange("C", 81, 88) },
  { label: "Abnormal uterine bleeding", matches: inCategories("N92", "N93") },
];

export const groupCodesByFamily = (codes: readonly string[]): JudgeGroup[] =>
  FAMILIES.flatMap(({ label, matches }) => {
    const members = unique(codes.filter(matches));

    return members.length >= 2 ? [{ label, codes: members }] : [];
  });

const CANCER_PAIRINGS: readonly { anchor: CodeRule; partner: CodeRule }[] = [
  { anchor: inCategories("C80"), partner: isMalignantNeoplasmIcd10Code },
  { anchor: isExactCode("C79.9"), partner: isSecondaryMalignantNeoplasmIcd10Code },
  { anchor: inRange("D", 0, 9), partner: isMalignantNeoplasmIcd10Code },
  { anchor: isExactCode("Z85.0"), partner: inRange("C", 15, 26) },
  { anchor: isExactCode("Z85.1"), partner: inRange("C", 33, 34) },
  { anchor: isExactCode("Z85.2"), partner: anyOf(inRange("C", 30, 32), inRange("C", 37, 39)) },
  { anchor: isExactCode("Z85.3"), partner: inCategories("C50") },
  { anchor: isExactCode("Z85.4"), partner: inRange("C", 51, 63) },
  { anchor: isExactCode("Z85.5"), partner: inRange("C", 64, 68) },
  { anchor: isExactCode("Z85.6"), partner: inRange("C", 91, 95) },
  { anchor: isExactCode("Z85.7"), partner: anyOf(inRange("C", 81, 90), inCategories("C96")) },
  // Deliberately narrower than the ICD-10 residual: product scope is these sites only, so C44 and C69–C79 stay unpaired.
  { anchor: isExactCode("Z85.8"), partner: anyOf(inRange("C", 0, 14), inCategories("C43", "C73")) },
  { anchor: isExactCode("Z85.9"), partner: isMalignantNeoplasmIcd10Code },
  { anchor: inCategories("D37"), partner: anyOf(isExactCode("Z85.0"), isExactCode("Z86.0")) },
  { anchor: inCategories("D38"), partner: anyOf(isExactCode("Z85.1"), isExactCode("Z86.0")) },
  { anchor: inCategories("D39", "D40"), partner: anyOf(isExactCode("Z85.4"), isExactCode("Z86.0")) },
  { anchor: inCategories("D41"), partner: anyOf(isExactCode("Z85.5"), isExactCode("Z86.0")) },
  { anchor: isExactCode("D48.6"), partner: anyOf(isExactCode("Z85.3"), isExactCode("Z86.0")) },
  { anchor: inCategories("D00", "D01"), partner: isExactCode("Z85.0") },
  {
    anchor: anyOf(isExactCode("D12.6"), isExactCode("D13.9")),
    partner: anyOf(isExactCode("Z85.0"), isExactCode("Z86.0")),
  },
];

export const groupCodesByCancerPairing = (codes: readonly string[]): JudgeGroup[] => {
  const pairs = CANCER_PAIRINGS.flatMap(({ anchor, partner }) =>
    unique(codes.filter(anchor)).flatMap((anchored) =>
      unique(codes.filter(partner))
        .filter((partnered) => partnered !== anchored)
        .map((partnered): JudgeGroup => ({ label: "Cancer", codes: [anchored, partnered] })),
    ),
  );

  return mergeLinked(pairs, codes);
};

type SameSiteNeoplasm = {
  organ: string;
  primary: CodeRule;
  uncertain: CodeRule;
  secondary: CodeRule;
  benign?: CodeRule;
};

const exactCodes = (...codes: string[]): CodeRule => anyOf(...codes.map(isExactCode));

const SAME_SITE_NEOPLASMS: readonly SameSiteNeoplasm[] = [
  {
    organ: "Oesophagus",
    primary: inCategories("C15"),
    uncertain: isExactCode("D37.7"),
    secondary: isExactCode("C78.8"),
    benign: isExactCode("D13.0"),
  },
  {
    organ: "Stomach",
    primary: inCategories("C16"),
    uncertain: isExactCode("D37.1"),
    secondary: isExactCode("C78.8"),
    benign: isExactCode("D13.1"),
  },
  {
    organ: "Small intestine",
    primary: inCategories("C17"),
    uncertain: isExactCode("D37.2"),
    secondary: isExactCode("C78.4"),
    benign: exactCodes("D13.2", "D13.3"),
  },
  {
    organ: "Appendix",
    primary: isExactCode("C18.1"),
    uncertain: isExactCode("D37.3"),
    secondary: isExactCode("C78.5"),
    benign: isExactCode("D12.1"),
  },
  {
    organ: "Colon",
    primary: inCategories("C18"),
    uncertain: isExactCode("D37.4"),
    secondary: isExactCode("C78.5"),
    benign: exactCodes("D12.0", "D12.1", "D12.2", "D12.3", "D12.4", "D12.5", "D12.6"),
  },
  {
    organ: "Rectosigmoid junction",
    primary: inCategories("C19"),
    uncertain: isExactCode("D37.5"),
    secondary: isExactCode("C78.5"),
    benign: isExactCode("D12.7"),
  },
  {
    organ: "Rectum",
    primary: inCategories("C20"),
    uncertain: isExactCode("D37.5"),
    secondary: isExactCode("C78.5"),
    benign: isExactCode("D12.8"),
  },
  {
    organ: "Anus and anal canal",
    primary: inCategories("C21"),
    uncertain: isExactCode("D37.7"),
    secondary: isExactCode("C78.5"),
    benign: isExactCode("D12.9"),
  },
  {
    organ: "Liver",
    primary: inCategories("C22"),
    uncertain: isExactCode("D37.6"),
    secondary: isExactCode("C78.7"),
    benign: isExactCode("D13.4"),
  },
  {
    organ: "Gallbladder and bile ducts",
    primary: inCategories("C23", "C24"),
    uncertain: isExactCode("D37.6"),
    secondary: isExactCode("C78.8"),
    benign: isExactCode("D13.5"),
  },
  {
    organ: "Pancreas",
    primary: inCategories("C25"),
    uncertain: isExactCode("D37.7"),
    secondary: isExactCode("C78.8"),
    benign: isExactCode("D13.6"),
  },
  {
    organ: "Peritoneum",
    primary: inCategories("C48"),
    uncertain: isExactCode("D48.4"),
    secondary: isExactCode("C78.6"),
  },
  {
    organ: "Lung",
    primary: inCategories("C34"),
    uncertain: isExactCode("D38.1"),
    secondary: isExactCode("C78.0"),
    benign: isExactCode("D14.3"),
  },
  {
    organ: "Breast",
    primary: inCategories("C50"),
    uncertain: isExactCode("D48.6"),
    secondary: isExactCode("C79.8"),
  },
  {
    organ: "Bladder",
    primary: inCategories("C67"),
    uncertain: isExactCode("D41.4"),
    secondary: isExactCode("C79.1"),
  },
  {
    organ: "Kidney",
    primary: inCategories("C64"),
    uncertain: isExactCode("D41.0"),
    secondary: isExactCode("C79.0"),
  },
  {
    organ: "Ovary",
    primary: inCategories("C56"),
    uncertain: isExactCode("D39.1"),
    secondary: isExactCode("C79.6"),
  },
];

export const groupCodesBySameSiteNeoplasm = (codes: readonly string[]): JudgeGroup[] => {
  const groups = SAME_SITE_NEOPLASMS.flatMap(({ organ, primary, uncertain, secondary, benign }): JudgeGroup[] => {
    const primaries = unique(codes.filter(primary));
    const uncertains = unique(codes.filter(uncertain));
    const secondaries = unique(codes.filter(secondary));
    const benigns = benign ? unique(codes.filter(benign)) : [];
    const behaviours = [primaries, uncertains, secondaries].filter((members) => members.length > 0).length;
    // A benign code joins only beside an uncertain one: primary plus benign alone is two separate lesions (coders kept both 21 of 49).
    const qualifies = behaviours >= 2 || (uncertains.length > 0 && benigns.length > 0);

    return qualifies
      ? [
          {
            label: `${organ} neoplasm`,
            codes: [...primaries, ...uncertains, ...secondaries, ...(uncertains.length > 0 ? benigns : [])],
          },
        ]
      : [];
  });

  return mergeLinked(groups, codes);
};

/** Groups of codes that may record one condition twice, one group per condition; everything else bypasses the judge. */
export const groupCodesByDomain = (codes: readonly string[], descriptionOf: DescriptionLookup): JudgeGroup[] => {
  const sendable = codes.filter((code) => !isNeverSentToJudge(code));

  return mergeLinked(
    [
      ...groupCodesByCategory(sendable, descriptionOf),
      ...groupCodesByFamily(sendable),
      ...groupCodesBySameSiteNeoplasm(sendable),
      ...groupCodesByCancerPairing(codes),
    ],
    codes,
  );
};
